import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Typography,
} from '@mui/joy';
import type { DooGlass, DooSupplier, StatusFilter } from '../types';
import {
  createGlass,
  deleteGlass,
  formatDependents,
  glassDependents,
  listGlass,
  listSuppliers,
  updateGlass,
} from '../data/repository';
import { catalogueStatus, matchesStatusFilter } from '../data/status';
import { useAsyncList } from './NamedEntitySection';
import {
  ActionRow,
  AssetPathField,
  ConfirmDialog,
  DivestFields,
  OracleTable,
  StatusChip,
  StatusFilterSelect,
  Toolbar,
  TruncatedText,
} from './FormControls';

export function GlassSection() {
  const glass = useAsyncList(listGlass);
  const suppliers = useAsyncList(listSuppliers);
  const [filter, setFilter] = useState<StatusFilter>('active');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<DooGlass | null>(null);
  const [name, setName] = useState('');
  const [supplierId, setSupplierId] = useState<string | null>(null);
  const [assetPath, setAssetPath] = useState('');
  const [divest, setDivest] = useState(false);
  const [deprecatedDate, setDeprecatedDate] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteRow, setDeleteRow] = useState<DooGlass | null>(null);

  const supplierName = (id: string | null, rows: DooSupplier[]) =>
    rows.find((row) => row.supplier_id === id)?.supplier_name || '—';

  const visible = useMemo(
    () => glass.rows.filter((row) => matchesStatusFilter(row, filter)),
    [glass.rows, filter]
  );

  const payload = () => ({
    glass_name: name.trim(),
    supplier_id: supplierId,
    asset_path: assetPath.trim() || null,
    divest,
    deprecated_date: deprecatedDate || null,
  });

  const openCreate = () => {
    setEditing(null);
    setName('');
    setSupplierId(null);
    setAssetPath('');
    setDivest(false);
    setDeprecatedDate('');
    setFormError(null);
    setOpen(true);
  };

  return (
    <Box>
      {glass.error ? <Alert color="danger" sx={{ mb: 2 }}>{glass.error}</Alert> : null}
      {formError && !open ? <Alert color="warning" sx={{ mb: 2 }}>{formError}</Alert> : null}
      <Toolbar>
        <StatusFilterSelect value={filter} onChange={setFilter} />
        <Button onClick={openCreate}>Add glass</Button>
      </Toolbar>
      <OracleTable>
        <thead>
          <tr>
            <th>Glass</th>
            <th>Supplier</th>
            <th style={{ width: 280 }}>Asset path</th>
            <th>Status</th>
            <th style={{ width: 140 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {glass.loading ? (
            <tr><td colSpan={5}>Loading…</td></tr>
          ) : visible.length === 0 ? (
            <tr><td colSpan={5}>No glass yet.</td></tr>
          ) : (
            visible.map((row) => (
              <tr key={row.glass_id}>
                <td>{row.glass_name}</td>
                <td>{supplierName(row.supplier_id, suppliers.rows)}</td>
                <td>
                  <TruncatedText value={row.asset_path} maxWidth={260} />
                </td>
                <td><StatusChip status={catalogueStatus(row)} /></td>
                <td>
                  <ActionRow>
                    <Button
                      size="sm"
                      variant="plain"
                      onClick={() => {
                        setEditing(row);
                        setName(row.glass_name);
                        setSupplierId(row.supplier_id);
                        setAssetPath(row.asset_path || '');
                        setDivest(Boolean(row.divest));
                        setDeprecatedDate(row.deprecated_date?.slice(0, 10) || '');
                        setFormError(null);
                        setOpen(true);
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="plain"
                      color="danger"
                      onClick={async () => {
                        const dependents = await glassDependents(row.glass_id);
                        if (dependents.length) {
                          setFormError(
                            `Cannot delete “${row.glass_name}” while it is used by ${formatDependents(dependents)}. Divest or deprecate it instead.`
                          );
                          return;
                        }
                        setDeleteRow(row);
                      }}
                    >
                      Delete
                    </Button>
                  </ActionRow>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </OracleTable>

      <Modal open={open} onClose={() => setOpen(false)}>
        <ModalDialog sx={{ minWidth: 480 }}>
          <ModalClose />
          <Typography level="title-lg">{editing ? 'Edit glass' : 'New glass'}</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
            {formError ? <Alert color="danger">{formError}</Alert> : null}
            <Input placeholder="Glass name" value={name} onChange={(e) => setName(e.target.value)} />
            <Select
              placeholder="Supplier"
              value={supplierId}
              onChange={(_, value) => setSupplierId((value as string) || null)}
            >
              {suppliers.rows.map((row) => (
                <Option key={row.supplier_id} value={row.supplier_id}>
                  {row.supplier_name}
                </Option>
              ))}
            </Select>
            <AssetPathField value={assetPath} onChange={setAssetPath} />
            <DivestFields
              divest={divest}
              deprecatedDate={deprecatedDate}
              onDivest={setDivest}
              onDeprecatedDate={setDeprecatedDate}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button variant="outlined" onClick={() => setOpen(false)}>Cancel</Button>
              <Button
                onClick={async () => {
                  if (!name.trim()) {
                    setFormError('Glass name is required.');
                    return;
                  }
                  try {
                    if (editing) await updateGlass(editing.glass_id, payload());
                    else await createGlass(payload());
                    setOpen(false);
                    glass.reload();
                  } catch (err) {
                    setFormError(err instanceof Error ? err.message : 'Save failed');
                  }
                }}
              >
                Save
              </Button>
            </Box>
          </Box>
        </ModalDialog>
      </Modal>

      <ConfirmDialog
        open={Boolean(deleteRow)}
        title="Delete glass?"
        message={deleteRow ? `Delete “${deleteRow.glass_name}”? Prefer deprecation for established products.` : ''}
        onClose={() => setDeleteRow(null)}
        onConfirm={async () => {
          if (!deleteRow) return;
          try {
            await deleteGlass(deleteRow.glass_id);
            setDeleteRow(null);
            glass.reload();
          } catch (err) {
            setDeleteRow(null);
            setFormError(err instanceof Error ? err.message : 'Delete failed');
          }
        }}
      />
    </Box>
  );
}
